"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { DEV_PRESETS, DevPresetKey } from "@/lib/dev/presets";

/* ═══════════════════════════════════════════════
   CONFIG — viewports, presets, routes
   ═══════════════════════════════════════════════ */

const BASELINE_PORT = Number(process.env.NEXT_PUBLIC_BASELINE_PORT) || 4000;
const PREVCOMMIT_PORT =
  Number(process.env.NEXT_PUBLIC_PREVCOMMIT_PORT) || 3500;

interface ReviewCombo {
  route: string;
  preset: DevPresetKey;
  note?: string;
  viewports?: string[]; // e.g. ["iPhone 15", "iPad"] — filters which viewports to show
}

const VIEWPORTS = [
  { name: "iPhone SE", width: 375, height: 667 },
  { name: "iPhone 15", width: 393, height: 852 },
  { name: "iPad", width: 810, height: 1080 },
  { name: "Desktop", width: 1440, height: 900 },
  { name: "Native iOS", width: 393, height: 852, native: "ios" as const },
  { name: "Native iPad", width: 810, height: 1080, native: "ios" as const },
  { name: "Native Android", width: 412, height: 915, native: "android" as const },
];

const PRESET_GROUPS: { label: string; keys: DevPresetKey[] }[] = [
  { label: "Overig", keys: ["none", "not_logged_in"] },
  {
    label: "Student — basis",
    keys: [
      "student_no_sub",
      "student_trial",
      "student_trial_ending",
      "student_trial_expired",
    ],
  },
  {
    label: "Student — abo",
    keys: [
      "student_active_sub",
      "student_active_sub_linked",
      "student_canceled_sub",
      "student_pending_sub",
    ],
  },
  {
    label: "Student — speciaal",
    keys: [
      "student_chargeback_md06",
      "student_chargeback_am04",
      "student_chargeback_ms02",
      "student_chargeback_ac06",
      "student_chargeback_sl01",
      "student_unlimited",
    ],
  },
  {
    label: "Ouders",
    keys: [
      "parent_no_sub",
      "parent_with_sub",
      "parent_canceled_sub",
      "parent_pending_sub",
      "parent_trial",
      "parent_chargeback_ms02",
      "parent_chargeback_ac06",
      "parent_chargeback_sl01",
    ],
  },
];

const ALL_PRESET_KEYS = PRESET_GROUPS.flatMap((g) => g.keys);

/** Route x preset matrix: which presets to test per route.
 *  Elke route bevat ALLE presets die een visueel verschil opleveren. */
const ROUTE_MATRIX: {
  group: string;
  routes: {
    path: string;
    label: string;
    presets: DevPresetKey[];
    note?: string;
  }[];
}[] = [
  {
    group: "Local checkout design",
    routes: [{
      path: "/dev/combined-checkout",
      label: "Combined child checkout",
      presets: ["not_logged_in"],
      note: "Local design only: email, optional extra child, one simulated bank confirmation, separate activation emails."
    }]
  },
  {
    group: "Landing & Home",
    routes: [
      { path: "/voor-leerlingen", label: "Voor leerlingen", presets: ["not_logged_in"] },
      {
        path: "/",
        label: "Home",
        presets: [
          "not_logged_in",
          "student_no_sub",
          "student_trial",
          "student_trial_expired",
          "student_active_sub",
          "student_active_sub_linked",
          "student_unlimited",
          "parent_no_sub",
          "parent_with_sub",
        ],
        note: "Niet ingelogd: Landing | Premium: PremiumLanding | Ouder: hub bij actief of gekoppeld abonnement",
      },
      {
        path: "/hoewerktwiswiz",
        label: "Hoe werkt WisWiz",
        presets: ["not_logged_in", "student_no_sub", "student_active_sub"],
        note: "Topbar: login/register vs account knop vs abo CTA",
      },
    ],
  },
  {
    group: "Authenticatie",
    routes: [
      {
        path: "/aanmelden",
        label: "Aanmelden (login)",
        presets: ["not_logged_in"],
      },
      {
        path: "/sessie_verlopen",
        label: "Sessie verlopen",
        presets: ["not_logged_in"],
      },
    ],
  },
  {
    group: "Library & Content",
    routes: [
      {
        path: "/library",
        label: "Library (profiel selectie)",
        presets: [
          "not_logged_in",
          "student_no_sub",
          "student_trial",
          "student_active_sub",
          "student_unlimited",
          "parent_no_sub",
          "parent_with_sub",
        ],
        note: "Niet ingelogd: login form | Ingelogd: SelectieMenu | Ouder: 'Zelf even meekijken?'",
      },
      {
        path: "/library/vraag/24/a",
        label: "Library · Vraag met paywall",
        presets: ["student_no_sub", "student_trial_expired", "parent_no_sub"],
        note: "PaymentGate: desktop = blurred stappenplan + floating card; mobile test = full-screen overlay",
      },
    ],
  },
  {
    group: "Account — overzicht",
    routes: [
      {
        path: "/account",
        label: "Account pagina",
        presets: [
          "student_no_sub",
          "student_trial",
          "student_trial_ending",
          "student_trial_expired",
          "student_active_sub",
          "student_active_sub_linked",
          "student_canceled_sub",
          "student_pending_sub",
                  "student_chargeback_md06",
          "student_chargeback_am04",
          "student_chargeback_ms02",
          "student_chargeback_ac06",
          "student_chargeback_sl01",
          "student_unlimited",
          "parent_no_sub",
          "parent_with_sub",
          "parent_past_due",
          "parent_canceled_sub",
          "parent_trial",
        ],
        note: "AccountStatusCard (8 prioriteitslevels) + SubscriptionCard + ChargebackCard",
      },
    ],
  },
  {
    group: "Account — opzeggen",
    routes: [
      {
        path: "/account/afmelden",
        label: "Abonnement opzeggen",
        presets: [
          "student_active_sub",
          "student_active_sub_linked",
          "student_canceled_sub",
          "parent_with_sub",
          "parent_canceled_sub",
        ],
        note: "AfmeldForm: student vs ouder config | Direct vs linked sub | isMobile layout",
      },
    ],
  },
  {
    group: "Abonnement & Checkout",
    routes: [
      {
        path: "/abonnement",
        label: "Abonnement/pricing",
        presets: [
          "not_logged_in",
          "student_no_sub",
          "student_trial",
          "student_trial_expired",
          "student_active_sub",
          "student_canceled_sub",
          "parent_no_sub",
        ],
        note: "PricingDisplay + SubscriptionCTA: trial_expired wijzigt knoptekst",
      },
      {
        path: "/checkout",
        label: "Checkout",
        presets: [
          "not_logged_in",
          "student_no_sub",
          "student_trial",
          "student_trial_expired",
          "parent_no_sub",
          "parent_with_sub",
        ],
        note: "Niet ingelogd: redirect /aanmelden | Student: StudentCheckout | Ouder: ParentCheckout",
      },
      {
        path: "/aanmelden?intent=checkout",
        label: "Aanmelden (student)",
        presets: ["not_logged_in"],
      },
      {
        path: "/aanmelden?intent=checkout&source=ouders",
        label: "Aanmelden (ouder, direct)",
        presets: ["not_logged_in"],
      },
      {
        path: "/aanmelden?intent=checkout&token=dev_preview_token_abc123",
        label: "Aanmelden (ouder, uitgenodigd)",
        presets: ["not_logged_in"],
      },
      {
        path: "/checkout?step=plan",
        label: "Student plan selectie",
        presets: [
          "student_no_sub",
          "student_trial",
          "student_trial_expired",
        ],
      },
      {
        path: "/checkout?step=invite",
        label: "Student nodigt ouder uit",
        presets: [
          "student_no_sub",
          "student_trial",
          "student_trial_expired",
        ],
      },
      {
        path: "/checkout?step=checkout",
        label: "Student checkout",
        presets: [
          "student_no_sub",
          "student_trial",
          "student_trial_expired",
        ],
      },
      {
        path: "/checkout",
        label: "Ouder checkout (direct)",
        presets: ["parent_no_sub", "parent_trial", "parent_trial_expired"],
      },
      {
        path: "/checkout?token=dev_preview_token_abc123",
        label: "Ouder checkout (na uitnodiging)",
        presets: ["parent_no_sub"],
      },
      {
        path: "/checkout?resume=1",
        label: "Ouder checkout (resume)",
        presets: ["parent_canceled_sub", "parent_pending_sub"],
      },
    ],
  },
  {
    group: "Abonnement beheren (ouder)",
    routes: [
      {
        path: "/manage-subscription/dev",
        label: "Abonnement beheren",
        presets: [
          "parent_with_sub",
          "parent_past_due",
          "parent_canceled_sub",
          "parent_trial",
        ],
        note: "Linked student info | Past due: betalingsprobleem | Opgezegd: valid_until | Trial: geen betaaldata",
      },
    ],
  },
  {
    group: "Betaling",
    routes: [
      {
        path: "/payment/gelukt",
        label: "Betaling gelukt",
        presets: [
          "student_active_sub",
          "parent_with_sub",
          "parent_trial",
        ],
        note: "Student: success + 'Kies hoofdstuk' | Ouder: success + ShareInvite",
      },
    ],
  },
  {
    group: "Statische pagina's",
    routes: [
      {
        path: "/contact",
        label: "Contact",
        presets: ["not_logged_in", "student_active_sub"],
        note: "Topbar verschilt: login knoppen vs account knop",
      },
      {
        path: "/hoewerktwiswiz",
        label: "Hoe werkt WisWiz",
        presets: ["not_logged_in", "student_active_sub"],
      },
      {
        path: "/algemene_voorwaarden",
        label: "Algemene voorwaarden",
        presets: ["not_logged_in"],
      },
      {
        path: "/privacy_verklaring",
        label: "Privacy verklaring",
        presets: ["not_logged_in"],
      },
    ],
  },
];

/** Build iframe src with _devpreview param (and _devnative for native viewports) */
function buildPreviewSrc(
  path: string,
  native?: string
): string {
  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.set("_devpreview", "1");
    if (native) url.searchParams.set("_devnative", native);
    return url.pathname + url.search + url.hash;
  } catch {
    const sep = path.includes("?") ? "&" : "?";
    const nativeParam = native ? `&_devnative=${native}` : "";
    return `${path}${sep}_devpreview=1${nativeParam}`;
  }
}

/** Read current dev_preset cookie value */
function getPresetCookie(): DevPresetKey {
  const match = document.cookie.match(/(?:^|;\s*)dev_preset=([^;]+)/);
  return (match?.[1] as DevPresetKey) ?? "none";
}

/** Set or clear the dev_preset cookie */
function setPresetCookie(preset: DevPresetKey) {
  if (preset === "none") {
    document.cookie = "dev_preset=; path=/; max-age=0";
  } else {
    document.cookie = `dev_preset=${preset}; path=/`;
  }
}

/* ═══════════════════════════════════════════════
   ENTRY — environment gate
   ═══════════════════════════════════════════════ */

export default function DevPreviewPage() {
  if (process.env.NODE_ENV !== "development") {
    return (
      <div style={S.center}>
        <p style={{ fontFamily: "system-ui" }}>
          Alleen beschikbaar in development.
        </p>
      </div>
    );
  }
  return <PreviewApp />;
}

/* ═══════════════════════════════════════════════
   MAIN APP — sidebar + main area
   ═══════════════════════════════════════════════ */

type ViewTab = "preview" | "matrix";

function PreviewApp() {
  // State
  const [tab, setTab] = useState<ViewTab>("preview");
  const [path, setPath] = useState("/");
  const [inputValue, setInputValue] = useState("/");
  const [scale, setScale] = useState(0.5);
  const [activeViewports, setActiveViewports] = useState<Set<number>>(
    () => new Set(VIEWPORTS.map((_, i) => i))
  );
  const [preReviewViewports, setPreReviewViewports] = useState<Set<number> | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activePreset, setActivePresetState] = useState<DevPresetKey>("none");

  // Refresh key: incremented on preset change -> forces iframe remount
  const [refreshKey, setRefreshKey] = useState(0);
  const [comparePort, setComparePort] = useState<number | null>(null);
  const [baselineAlive, setBaselineAlive] = useState<boolean | null>(null);
  const [prevcommitAlive, setPrevcommitAlive] = useState<boolean | null>(null);

  // Review combos from .dev-review.json
  const [reviewCombos, setReviewCombos] = useState<ReviewCombo[]>([]);
  const [reviewIndex, setReviewIndex] = useState(-1); // -1 = review not active
  const [approvedItems, setApprovedItems] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem("dev-review-approved");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // Read cookie + ?route= param on mount, and fetch review config
  useEffect(() => {
    setActivePresetState(getPresetCookie());
    const routeParam = new URLSearchParams(window.location.search).get("route");
    if (routeParam) {
      setPath(routeParam);
      setInputValue(routeParam);
    }
    // Fetch review combos
    fetch("/api/dev/review-config")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setReviewCombos(data);
      })
      .catch(() => {});
  }, []);

  // Check if baseline server is reachable — polls until alive, then stops
  // Continuous health checks for comparison servers
  useServerHealth(BASELINE_PORT, setBaselineAlive);
  useServerHealth(PREVCOMMIT_PORT, setPrevcommitAlive);

  const presetLabel = DEV_PRESETS[activePreset]?.label ?? "Geen";

  // --- Actions ---

  const setActivePreset = useCallback((preset: DevPresetKey) => {
    setPresetCookie(preset);
    setActivePresetState(preset);
    setRefreshKey((k) => k + 1);
  }, []);

  const navigateTo = useCallback((newPath: string) => {
    setInputValue(newPath);
    setPath(newPath);
  }, []);

  const toggleViewport = useCallback((index: number) => {
    setActiveViewports((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }, []);

  const handleReloadAll = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const toggleApproval = useCallback((idx: number) => {
    setApprovedItems((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      try { localStorage.setItem("dev-review-approved", JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  const clearAllApprovals = useCallback(() => {
    setApprovedItems(new Set());
    try { localStorage.removeItem("dev-review-approved"); } catch {}
  }, []);

  // Navigate to a review combo by index
  const goToReview = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= reviewCombos.length) return;
      // Save current viewports before first review navigation
      setPreReviewViewports((prev) => prev ?? activeViewports);
      setReviewIndex(idx);
      const combo = reviewCombos[idx];
      setActivePreset(combo.preset);
      navigateTo(combo.route);
      setTab("preview");
      // Apply per-item viewport filter if specified
      if (combo.viewports) {
        const filtered = new Set<number>();
        combo.viewports.forEach((name) => {
          const i = VIEWPORTS.findIndex((vp) => vp.name === name);
          if (i >= 0) filtered.add(i);
        });
        if (filtered.size > 0) setActiveViewports(filtered);
      } else if (preReviewViewports) {
        setActiveViewports(preReviewViewports);
      }
    },
    [reviewCombos, setActivePreset, navigateTo, activeViewports, preReviewViewports]
  );

  // Auto-navigate when reviewIndex changes (including initial load)
  useEffect(() => {
    if (reviewIndex >= 0 && reviewIndex < reviewCombos.length) {
      const combo = reviewCombos[reviewIndex];
      setActivePreset(combo.preset);
      navigateTo(combo.route);
      // Apply per-item viewport filter
      if (combo.viewports) {
        const filtered = new Set<number>();
        combo.viewports.forEach((name) => {
          const i = VIEWPORTS.findIndex((vp) => vp.name === name);
          if (i >= 0) filtered.add(i);
        });
        if (filtered.size > 0) setActiveViewports(filtered);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewIndex]);

  // Matrix cell click -> set preset + route, switch to preview
  const handleMatrixClick = useCallback(
    (route: string, preset: DevPresetKey) => {
      setActivePreset(preset);
      setInputValue(route);
      setPath(route);
      setTab("preview");
      setReviewIndex(-1); // exit review mode
    },
    [setActivePreset]
  );

  // Keyboard: arrow up/down to cycle presets, left/right for review, R to reload
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      )
        return;

      if (e.key === "ArrowLeft" && reviewCombos.length > 0) {
        e.preventDefault();
        // Find previous unapproved item (wrap around)
        let prev = reviewIndex;
        for (let i = 0; i < reviewCombos.length; i++) {
          prev = prev <= 0 ? reviewCombos.length - 1 : prev - 1;
          if (!approvedItems.has(prev)) break;
        }
        goToReview(prev);
      }
      if (e.key === "ArrowRight" && reviewCombos.length > 0) {
        e.preventDefault();
        // Find next unapproved item (wrap around)
        let next = reviewIndex;
        for (let i = 0; i < reviewCombos.length; i++) {
          next = next >= reviewCombos.length - 1 ? 0 : next + 1;
          if (!approvedItems.has(next)) break;
        }
        goToReview(next);
      }
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const idx = ALL_PRESET_KEYS.indexOf(activePreset);
        const next =
          e.key === "ArrowDown"
            ? (idx + 1) % ALL_PRESET_KEYS.length
            : (idx - 1 + ALL_PRESET_KEYS.length) % ALL_PRESET_KEYS.length;
        setActivePreset(ALL_PRESET_KEYS[next]);
        setReviewIndex(-1); // exit review mode on manual preset change
      }
      if (e.key === "r" && !e.metaKey && !e.ctrlKey) {
        handleReloadAll();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activePreset, setActivePreset, handleReloadAll, reviewCombos, reviewIndex, goToReview, approvedItems]);

  const isOverrideActive = activePreset !== "none";

  return (
    <div style={S.root}>
      {/* --- SIDEBAR (hidden during active review) --- */}
      {!sidebarCollapsed && reviewIndex < 0 && (
        <div style={S.sidebar}>
          <div style={S.sidebarInner}>
            {/* Sidebar header */}
            <div style={S.sidebarHeader}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Dev Preview
              </span>
              <button
                onClick={() => setSidebarCollapsed(true)}
                style={S.collapseBtn}
                title="Collapse sidebar"
              >
                &laquo;
              </button>
            </div>

            {/* Active state indicator */}
            <div
              style={{
                ...S.statusBanner,
                background: isOverrideActive ? "#92400e" : "#1e3a5f",
                borderColor: isOverrideActive ? "#f59e0b" : "#374151",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  color: "#9ca3af",
                  letterSpacing: "0.05em",
                }}
              >
                Actieve staat
              </div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{presetLabel}</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>{path}</div>
            </div>

            {/* Preset groups */}
            <div style={S.sidebarSection}>
              <SectionLabel>
                Gebruikersstatus{" "}
                <span style={{ color: "#6b7280", fontWeight: 400 }}>
                  &#8593;&#8595;
                </span>
              </SectionLabel>
              {PRESET_GROUPS.map((group) => (
                <div key={group.label} style={{ marginBottom: 8 }}>
                  <div style={S.groupLabel}>{group.label}</div>
                  {group.keys.map((key) => {
                    const preset = DEV_PRESETS[key];
                    const isActive = activePreset === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setActivePreset(key)}
                        style={{
                          ...S.presetBtn,
                          borderColor: isActive ? "#3b82f6" : "transparent",
                          background: isActive ? "#1e3a5f" : "transparent",
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: isActive ? "#3b82f6" : "#4b5563",
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontSize: 12 }}>{preset.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Viewport toggles */}
            <div style={S.sidebarSection}>
              <SectionLabel>Viewports</SectionLabel>
              {VIEWPORTS.map((vp, i) => (
                <label
                  key={vp.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 0",
                    fontSize: 12,
                    cursor: "pointer",
                    color: "#d1d5db",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={activeViewports.has(i)}
                    onChange={() => toggleViewport(i)}
                    style={{ accentColor: "#3b82f6" }}
                  />
                  {vp.name}{" "}
                  <span style={{ color: "#6b7280" }}>
                    {vp.width}&times;{vp.height}
                  </span>
                </label>
              ))}
            </div>

            {/* Scale */}
            <div style={S.sidebarSection}>
              <SectionLabel>Schaal {Math.round(scale * 100)}%</SectionLabel>
              <input
                type="range"
                min="0.25"
                max="1"
                step="0.05"
                value={scale}
                onChange={(e) => setScale(parseFloat(e.target.value))}
                style={{ width: "100%", accentColor: "#3b82f6" }}
              />
            </div>

            {/* Keyboard hints */}
            <div
              style={{
                ...S.sidebarSection,
                borderTop: "none",
                color: "#4b5563",
                fontSize: 11,
              }}
            >
              <div>&#8593;&#8595; Wissel preset</div>
              <div>&#8592;&#8594; Review vorige/volgende</div>
              <div>R&ensp; Reload iframes</div>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed sidebar toggle */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          style={S.expandBtn}
          title="Expand sidebar"
        >
          &raquo;
        </button>
      )}

      {/* --- MAIN AREA --- */}
      <div style={S.main}>
        {/* Review top bar (replaces normal controls when review active) */}
        {reviewIndex >= 0 && reviewCombos.length > 0 && (
          <ReviewTopBar
            combos={reviewCombos}
            currentIndex={reviewIndex}
            onNavigate={goToReview}
            onClose={() => {
              setReviewIndex(-1);
              if (preReviewViewports) {
                setActiveViewports(preReviewViewports);
                setPreReviewViewports(null);
              }
            }}
            comparePort={comparePort}
            onCompareChange={setComparePort}
            baselineAlive={baselineAlive ?? false}
            prevcommitAlive={prevcommitAlive ?? false}
            approvedItems={approvedItems}
            onToggleApproval={toggleApproval}
            onClearApprovals={clearAllApprovals}
          />
        )}

        {/* Tab bar + route bar (hidden during active review) */}
        {reviewIndex < 0 && <div style={S.topBar}>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 0 }}>
            {(["preview", "matrix"] as ViewTab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  ...S.tabBtn,
                  borderBottomColor: tab === t ? "#3b82f6" : "transparent",
                  color: tab === t ? "#f9fafb" : "#6b7280",
                }}
              >
                {t === "preview" ? "Preview" : "Matrix"}
              </button>
            ))}
          </div>

          {/* Route bar (only on preview tab) */}
          {tab === "preview" && (
            <div style={S.routeBar}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setPath(inputValue);
                    setRefreshKey((k) => k + 1);
                  }
                }}
                placeholder="/path"
                style={S.routeInput}
              />
              <button
                onClick={() => {
                  setPath(inputValue);
                  setRefreshKey((k) => k + 1);
                }}
                style={S.goBtn}
              >
                Go
              </button>

              {/* Quick routes */}
              {[
                "/",
                "/library",
                "/checkout",
                "/account",
                "/account/afmelden",
                "/abonnement",
                "/aanmelden",
                "/manage-subscription/dev",
                "/payment/gelukt",
                "/hoewerktwiswiz",
                "/contact",
              ].map((route) => (
                <button
                  key={route}
                  onClick={() => navigateTo(route)}
                  style={{
                    ...S.quickRouteBtn,
                    background: path === route ? "#374151" : "transparent",
                    borderColor: path === route ? "#4b5563" : "#374151",
                  }}
                >
                  {route === "/" ? "Home" : route.replace("/", "")}
                </button>
              ))}

              {/* Compare toggle */}
              <button
                onClick={() => setComparePort(comparePort ? null : BASELINE_PORT)}
                style={{
                  ...S.quickRouteBtn,
                  background: comparePort ? "#7c3aed" : "transparent",
                  borderColor: comparePort ? "#7c3aed" : "#374151",
                  color: comparePort ? "white" : "#9ca3af",
                  fontWeight: comparePort ? 600 : 400,
                }}
                title={`Vergelijk met baseline server op port ${BASELINE_PORT}`}
              >
                {comparePort ? "Compare ON" : "Compare"}
              </button>

              {/* Reload */}
              <button
                onClick={handleReloadAll}
                style={S.reloadBtn}
                title="Reload alle iframes (R)"
              >
                &#8635;
              </button>
            </div>
          )}
        </div>}

        {/* Compare server warning */}
        {comparePort === BASELINE_PORT && baselineAlive === false && (
          <div style={{ padding: "8px 12px", background: "#7f1d1d", color: "#fca5a5", fontSize: 12, borderBottom: "1px solid #991b1b", display: "flex", alignItems: "center", gap: 8 }}>
            <span>Develop server niet actief.</span>
            <code
              style={{ background: "#450a0a", padding: "2px 8px", borderRadius: 3, cursor: "pointer", userSelect: "all" }}
              onClick={() => navigator.clipboard.writeText("cd website && npm run dev:baseline")}
            >
              npm run dev:baseline
            </code>
          </div>
        )}
        {comparePort === PREVCOMMIT_PORT && prevcommitAlive === false && (
          <div style={{ padding: "8px 12px", background: "#7f1d1d", color: "#fca5a5", fontSize: 12, borderBottom: "1px solid #991b1b", display: "flex", alignItems: "center", gap: 8 }}>
            <span>Vorige commit server niet actief (herstart na nieuwe commit).</span>
            <code
              style={{ background: "#450a0a", padding: "2px 8px", borderRadius: 3, cursor: "pointer", userSelect: "all" }}
              onClick={() => navigator.clipboard.writeText("cd website && npm run dev:prevcommit")}
            >
              npm run dev:prevcommit
            </code>
          </div>
        )}

        {/* Content area */}
        {tab === "preview" ? (
          <PreviewArea
            path={path}
            scale={scale}
            refreshKey={refreshKey}
            activeViewports={activeViewports}
            vertical={reviewIndex >= 0}
            comparePort={
              comparePort === BASELINE_PORT && baselineAlive
                ? comparePort
                : comparePort === PREVCOMMIT_PORT && prevcommitAlive
                  ? comparePort
                  : comparePort === null
                    ? null
                    : null
            }
            activePreset={activePreset}
          />
        ) : (
          <MatrixArea
            onCellClick={handleMatrixClick}
            activePreset={activePreset}
          />
        )}

        {/* Review start bar (collapsed, only when review not active) */}
        {reviewCombos.length > 0 && reviewIndex < 0 && createPortal(
          <div style={S.reviewFooterCollapsed}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={S.reviewBadge}>REVIEW</span>
              <span style={{ color: "#9ca3af", fontSize: 13 }}>
                {reviewCombos.length - approvedItems.size} van {reviewCombos.length} te reviewen
                {approvedItems.size > 0 && (
                  <span style={{ color: "#10b981" }}> ({approvedItems.size} goedgekeurd)</span>
                )}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {approvedItems.size > 0 && (
                <button onClick={clearAllApprovals} style={{ ...S.quickRouteBtn, color: "#ef4444", borderColor: "#ef4444" }}>
                  Reset approvals
                </button>
              )}
              <button onClick={() => {
                // Start at first unapproved item
                const firstUnapproved = reviewCombos.findIndex((_, i) => !approvedItems.has(i));
                goToReview(firstUnapproved >= 0 ? firstUnapproved : 0);
              }} style={S.reviewStartBtn}>
                {approvedItems.size > 0 ? "Hervat review" : "Start review"} &rarr;
              </button>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   REVIEW TOP BAR — dominant bar when review is active
   ═══════════════════════════════════════════════ */

type CompareMode = "none" | "develop" | "prevcommit";

function ReviewTopBar({
  combos,
  currentIndex,
  onNavigate,
  onClose,
  comparePort,
  onCompareChange,
  baselineAlive,
  prevcommitAlive,
  approvedItems,
  onToggleApproval,
  onClearApprovals,
}: {
  combos: ReviewCombo[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  onClose: () => void;
  comparePort: number | null;
  onCompareChange: (port: number | null) => void;
  baselineAlive: boolean;
  prevcommitAlive: boolean;
  approvedItems: Set<number>;
  onToggleApproval: (idx: number) => void;
  onClearApprovals: () => void;
}) {
  const combo = combos[currentIndex];
  const presetData = combo ? DEV_PRESETS[combo.preset] : null;
  const presetLabel = presetData?.label ?? combo?.preset ?? "";
  const isCurrentApproved = approvedItems.has(currentIndex);

  const compareMode: CompareMode =
    comparePort === BASELINE_PORT
      ? "develop"
      : comparePort === PREVCOMMIT_PORT
        ? "prevcommit"
        : "none";

  const setCompareMode = (mode: CompareMode) => {
    switch (mode) {
      case "develop":
        onCompareChange(BASELINE_PORT);
        break;
      case "prevcommit":
        onCompareChange(PREVCOMMIT_PORT);
        break;
      default:
        onCompareChange(null);
    }
  };

  return (
    <div style={S.reviewTopBar}>
      {/* Row 1: what we're looking at */}
      <div style={S.reviewTopRow1}>
        {/* Navigation prev */}
        <button
          onClick={() => onNavigate(currentIndex <= 0 ? combos.length - 1 : currentIndex - 1)}
          style={S.reviewTopNavBtn}
          title="Vorige (←)"
        >
          &larr;
        </button>

        {/* Center: route + preset + note */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "center" }}>
          <span style={S.reviewBadge}>{currentIndex + 1} / {combos.length}</span>
          <code style={{ color: "#ffffff", fontSize: 18, fontFamily: "monospace", fontWeight: 700 }}>
            {combo.route}
          </code>
          <span style={{ color: "#475569", fontSize: 18 }}>&rarr;</span>
          <span style={{ color: "#93c5fd", fontSize: 16, fontWeight: 600 }}>{presetLabel}</span>
          {combo.note && (
            <span style={{ color: "#94a3b8", fontSize: 13 }}>
              &mdash; {combo.note}
            </span>
          )}
        </div>

        {/* Navigation next */}
        <button
          onClick={() => {
            // Find next unapproved
            let next = currentIndex;
            for (let i = 0; i < combos.length; i++) {
              next = next >= combos.length - 1 ? 0 : next + 1;
              if (!approvedItems.has(next)) break;
            }
            onNavigate(next);
          }}
          style={S.reviewTopNavBtn}
          title="Volgende (→)"
        >
          &rarr;
        </button>

        {/* Approve button */}
        <button
          onClick={() => onToggleApproval(currentIndex)}
          style={{
            ...S.reviewTopNavBtn,
            background: isCurrentApproved ? "#065f46" : "#1e293b",
            borderColor: isCurrentApproved ? "#10b981" : "#374151",
            color: isCurrentApproved ? "#10b981" : "#e2e8f0",
            fontSize: 14,
            padding: "8px 20px",
            marginLeft: 8,
          }}
          title={isCurrentApproved ? "Undo approval" : "Approve this screen"}
        >
          {isCurrentApproved ? "✓ Approved" : "Approve"}
        </button>

        {/* Close */}
        <button
          onClick={onClose}
          style={S.reviewTopCloseBtn}
          title="Stop review (Escape)"
        >
          &times;
        </button>
      </div>

      {/* Row 2: compare + dots */}
      <div style={S.reviewTopRow2}>
        {/* Compare mode */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ color: "#64748b", fontSize: 11, marginRight: 4 }}>Vergelijk:</span>
          {(
            [
              { mode: "none" as CompareMode, label: "Geen", color: "#3b82f6", alive: undefined as boolean | undefined },
              { mode: "develop" as CompareMode, label: "vs Develop", color: "#7c3aed", alive: baselineAlive as boolean | undefined },
              { mode: "prevcommit" as CompareMode, label: "vs Vorige commit", color: "#ea580c", alive: prevcommitAlive as boolean | undefined },
            ]
          ).map(({ mode, label, color, alive }) => (
            <button
              key={mode}
              onClick={() => setCompareMode(mode)}
              style={{
                ...S.reviewCompareBtn,
                background: compareMode === mode ? color : "transparent",
                borderColor: compareMode === mode ? color : "#374151",
                color: compareMode === mode ? "white" : "#9ca3af",
                fontWeight: compareMode === mode ? 600 : 400,
              }}
            >
              {label}
              {alive === false && (
                <span style={{ color: "#ef4444", marginLeft: 3, fontSize: 10 }}>&#9679;</span>
              )}
            </button>
          ))}
        </div>

        {/* Approved count + dots */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {approvedItems.size}/{combos.length} goedgekeurd
          </span>
          {approvedItems.size > 0 && (
            <button
              onClick={onClearApprovals}
              style={{ fontSize: 10, color: "#ef4444", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
            >
              reset
            </button>
          )}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {combos.map((c, i) => {
              const isApproved = approvedItems.has(i);
              const isCurrent = i === currentIndex;
              return (
                <button
                  key={i}
                  onClick={() => onNavigate(i)}
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    border: isCurrent ? "2px solid #60a5fa" : "2px solid transparent",
                    background: isApproved ? "#10b981" : isCurrent ? "#60a5fa" : "#374151",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 7,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title={`${c.route} → ${DEV_PRESETS[c.preset]?.label ?? c.preset}${isApproved ? " ✓" : ""}`}
                >
                  {isApproved ? "✓" : ""}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Continuously poll a server for health */
function useServerHealth(
  port: number,
  setAlive: (alive: boolean) => void,
) {
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      fetch(`http://localhost:${port}/`, { mode: "no-cors" })
        .then(() => { if (!cancelled) setAlive(true); })
        .catch(() => { if (!cancelled) setAlive(false); });
    };
    check();
    const interval = setInterval(check, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port]);
}

/* ═══════════════════════════════════════════════
   PREVIEW AREA — multi-viewport iframes
   ═══════════════════════════════════════════════ */

function PreviewArea({
  path,
  scale,
  refreshKey,
  activeViewports,
  comparePort,
  activePreset,
  vertical,
}: {
  path: string;
  scale: number;
  refreshKey: number;
  activeViewports: Set<number>;
  comparePort: number | null;
  activePreset: DevPresetKey;
  vertical?: boolean;
}) {
  const count = activeViewports.size;

  return (
    <div
      style={{
        flex: "1 1 0",
        minHeight: 0,
        maxHeight: "100%",
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        gap: 20,
        padding: 16,
        overflowY: "auto",
        overflowX: vertical ? "hidden" : "auto",
        justifyContent: vertical ? "flex-start" : count <= 2 ? "center" : "flex-start",
        alignItems: vertical ? "center" : "flex-start",
      }}
    >
      {VIEWPORTS.map((vp, i) => {
        if (!activeViewports.has(i)) return null;
        const nativeParam = "native" in vp ? vp.native : undefined;
        return (
          <DeviceFrame
            key={vp.name}
            vp={vp}
            scale={scale}
            refreshKey={refreshKey}
            path={path}
            nativeParam={nativeParam}
            comparePort={comparePort}
            activePreset={activePreset}
          />
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DEVICE FRAME — single viewport with nav + scroll
   ═══════════════════════════════════════════════ */

/** External scrollbar that syncs with an iframe's scroll position */
function IframeScrollbar({
  iframeRef,
  height,
}: {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  height: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbRatio, setThumbRatio] = useState(1); // viewport / scrollHeight
  const [scrollRatio, setScrollRatio] = useState(0); // scrollTop / maxScroll
  const dragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScroll = useRef(0);

  // Poll iframe scroll state (same-origin iframes)
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        const el = doc.documentElement;
        const scrollH = el.scrollHeight;
        const clientH = el.clientHeight;
        if (scrollH <= clientH) {
          setThumbRatio(1);
          return;
        }
        setThumbRatio(clientH / scrollH);
        setScrollRatio(el.scrollTop / (scrollH - clientH));
      } catch {}
    }, 100);
    return () => clearInterval(interval);
  }, [iframeRef]);

  const scrollIframe = useCallback((ratio: number) => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      const el = doc.documentElement;
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTop = Math.max(0, Math.min(maxScroll, ratio * maxScroll));
    } catch {}
  }, [iframeRef]);

  // Mouse drag handlers
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    dragStartY.current = e.clientY;
    dragStartScroll.current = scrollRatio;

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current || !trackRef.current) return;
      const trackH = trackRef.current.clientHeight;
      const thumbH = Math.max(20, thumbRatio * trackH);
      const scrollableTrack = trackH - thumbH;
      if (scrollableTrack <= 0) return;
      const dy = ev.clientY - dragStartY.current;
      const newRatio = Math.max(0, Math.min(1, dragStartScroll.current + dy / scrollableTrack));
      setScrollRatio(newRatio);
      scrollIframe(newRatio);
    };
    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [scrollRatio, thumbRatio, scrollIframe]);

  // Click on track to jump
  const onTrackClick = useCallback((e: React.MouseEvent) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const trackH = rect.height;
    const thumbH = Math.max(20, thumbRatio * trackH);
    const scrollableTrack = trackH - thumbH;
    if (scrollableTrack <= 0) return;
    const clickY = e.clientY - rect.top - thumbH / 2;
    const newRatio = Math.max(0, Math.min(1, clickY / scrollableTrack));
    setScrollRatio(newRatio);
    scrollIframe(newRatio);
  }, [thumbRatio, scrollIframe]);

  // Hide if no scroll needed
  if (thumbRatio >= 1) return null;

  const thumbH = Math.max(20, thumbRatio * height);
  const thumbTop = scrollRatio * (height - thumbH);

  return (
    <div
      ref={trackRef}
      onClick={onTrackClick}
      style={{
        width: 8,
        height,
        background: "#1f2937",
        borderRadius: 4,
        position: "relative",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <div
        onMouseDown={onMouseDown}
        style={{
          position: "absolute",
          top: thumbTop,
          left: 1,
          width: 6,
          height: thumbH,
          background: "#4b5563",
          borderRadius: 3,
          cursor: "grab",
        }}
      />
    </div>
  );
}

function DeviceFrame({
  vp,
  scale,
  refreshKey,
  path,
  nativeParam,
  comparePort,
  activePreset,
}: {
  vp: (typeof VIEWPORTS)[number];
  scale: number;
  refreshKey: number;
  path: string;
  nativeParam?: string;
  comparePort: number | null;
  activePreset: DevPresetKey;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const baselineRef = useRef<HTMLIFrameElement>(null);

  const goBack = () => {
    try { iframeRef.current?.contentWindow?.history.back(); } catch {}
    try { baselineRef.current?.contentWindow?.history.back(); } catch {}
  };
  const goForward = () => {
    try { iframeRef.current?.contentWindow?.history.forward(); } catch {}
    try { baselineRef.current?.contentWindow?.history.forward(); } catch {}
  };

  const navBtnStyle: React.CSSProperties = {
    padding: "2px 8px",
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 4,
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: 12,
    lineHeight: 1,
  };

  const frameHeight = vp.height * scale;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Header: name + nav buttons */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <button onClick={goBack} style={navBtnStyle} title="Back">&larr;</button>
        <span style={{ fontSize: 11, color: "#6b7280" }}>
          {vp.name} &mdash; {vp.width}&times;{vp.height}
        </span>
        <button onClick={goForward} style={navBtnStyle} title="Forward">&rarr;</button>
      </div>

      <div style={{ display: "flex", flexDirection: comparePort ? "row" : "column", gap: comparePort ? 8 : 0 }}>
        {/* Current version + scrollbar */}
        <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {comparePort && (
              <div style={{ textAlign: "center", fontSize: 9, color: "#10b981", fontWeight: 600, marginBottom: 2 }}>
                NIEUW (:{window.location.port || "3000"})
              </div>
            )}
            <div
              style={{
                width: vp.width * scale,
                height: frameHeight,
                border: comparePort ? "2px solid #10b981" : "2px solid #374151",
                borderRadius: 8,
                overflow: "hidden",
                background: "white",
                flexShrink: 0,
              }}
            >
              <iframe
                ref={iframeRef}
                key={`${vp.name}-${refreshKey}`}
                src={buildPreviewSrc(path, nativeParam)}
                allow="web-share"
                style={{
                  width: vp.width,
                  height: vp.height,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  border: "none",
                }}
                title={`${vp.name} preview`}
              />
            </div>
          </div>
          <IframeScrollbar iframeRef={iframeRef} height={frameHeight} />
        </div>

        {/* Baseline version + scrollbar (compare mode) */}
        {comparePort && (
          <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ textAlign: "center", fontSize: 9, color: "#f59e0b", fontWeight: 600, marginBottom: 2 }}>
                OUD (:{comparePort})
              </div>
              <div
                style={{
                  width: vp.width * scale,
                  height: frameHeight,
                  border: "2px solid #f59e0b",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "white",
                  flexShrink: 0,
                }}
              >
                <iframe
                  ref={baselineRef}
                  key={`${vp.name}-baseline-${refreshKey}`}
                  src={`http://localhost:${comparePort}${buildPreviewSrc(path, nativeParam)}&preset=${activePreset}`}
                  allow="web-share"
                  style={{
                    width: vp.width,
                    height: vp.height,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    border: "none",
                  }}
                  title={`${vp.name} baseline`}
                />
              </div>
            </div>
            <IframeScrollbar iframeRef={baselineRef} height={frameHeight} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MATRIX AREA — clickable route x preset grid
   ═══════════════════════════════════════════════ */

function MatrixArea({
  onCellClick,
  activePreset,
}: {
  onCellClick: (route: string, preset: DevPresetKey) => void;
  activePreset: DevPresetKey;
}) {
  return (
    <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
      {/* How-to banner */}
      <div style={S.infoBanner}>
        Klik op een preset-badge om die route + staat direct in de preview te
        openen. De actieve preset is blauw gemarkeerd.
      </div>

      {ROUTE_MATRIX.map((group) => (
        <div key={group.group} style={{ marginBottom: 20 }}>
          <div style={S.matrixGroupLabel}>{group.group}</div>
          {group.routes.map((route) => (
            <div key={route.path} style={S.matrixRow}>
              {/* Route info */}
              <div style={{ minWidth: 160 }}>
                <code style={S.routeCode}>{route.path}</code>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                  {route.label}
                  {route.note && (
                    <span style={{ color: "#4b5563" }}>
                      {" "}
                      &mdash; {route.note}
                    </span>
                  )}
                </div>
              </div>

              {/* Preset badges */}
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  flexWrap: "wrap",
                  flex: 1,
                }}
              >
                {route.presets.map((presetKey) => {
                  const preset = DEV_PRESETS[presetKey];
                  const isActive = activePreset === presetKey;
                  const color = presetKey.startsWith("parent")
                    ? {
                        bg: "#3b0764",
                        border: "#7c3aed",
                      }
                    : presetKey === "not_logged_in"
                      ? {
                          bg: "#1c1917",
                          border: "#78716c",
                        }
                      : { bg: "#0c4a6e", border: "#0284c7" };

                  return (
                    <button
                      key={presetKey}
                      onClick={() => onCellClick(route.path, presetKey)}
                      title={`Open ${route.path} als "${preset.label}"`}
                      style={{
                        padding: "3px 8px",
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: isActive ? "#3b82f6" : color.border,
                        background: isActive ? "#1e40af" : color.bg,
                        color: "#e2e8f0",
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: "nowrap",
                        transition: "all 0.1s",
                      }}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SMALL COMPONENTS
   ═══════════════════════════════════════════════ */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        textTransform: "uppercase",
        color: "#6b7280",
        fontWeight: 700,
        letterSpacing: "0.06em",
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STYLES — all inline for zero-dep dev tool
   ═══════════════════════════════════════════════ */

const S: Record<string, React.CSSProperties> = {
  center: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  root: {
    height: "100vh",
    display: "flex",
    background: "#111827",
    color: "#f9fafb",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontSize: 13,
    overflow: "hidden",
  },

  // Sidebar
  sidebar: {
    width: 220,
    flexShrink: 0,
    borderRight: "1px solid #1f2937",
    background: "#0f172a",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  sidebarInner: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
  },
  sidebarHeader: {
    padding: "10px 12px",
    borderBottom: "1px solid #1f2937",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  collapseBtn: {
    background: "none",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    fontSize: 16,
    padding: "0 4px",
  },
  expandBtn: {
    position: "absolute" as const,
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    background: "#1f2937",
    border: "1px solid #374151",
    borderLeft: "none",
    borderRadius: "0 6px 6px 0",
    color: "#9ca3af",
    cursor: "pointer",
    padding: "12px 6px",
    fontSize: 14,
    zIndex: 10,
  },
  statusBanner: {
    margin: "8px 8px 0",
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid",
    fontSize: 12,
  },
  sidebarSection: {
    padding: "10px 12px",
    borderTop: "1px solid #1f2937",
  },
  groupLabel: {
    fontSize: 10,
    textTransform: "uppercase" as const,
    color: "#4b5563",
    fontWeight: 600,
    letterSpacing: "0.05em",
    marginBottom: 2,
    marginTop: 4,
  },
  presetBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: "100%",
    padding: "4px 8px",
    borderRadius: 4,
    border: "1px solid transparent",
    background: "transparent",
    color: "#d1d5db",
    cursor: "pointer",
    textAlign: "left" as const,
    fontSize: 12,
  },

  // Main area
  main: {
    flex: "1 1 0",
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    position: "relative" as const,
  },
  topBar: {
    borderBottom: "1px solid #1f2937",
    flexShrink: 0,
  },
  tabBtn: {
    padding: "8px 16px",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "#6b7280",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  routeBar: {
    display: "flex",
    gap: 6,
    padding: "8px 12px",
    alignItems: "center",
    borderTop: "1px solid #1f2937",
    flexWrap: "wrap" as const,
  },
  routeInput: {
    width: 200,
    padding: "5px 10px",
    borderRadius: 5,
    border: "1px solid #374151",
    background: "#1f2937",
    color: "#f9fafb",
    fontSize: 13,
    outline: "none",
  },
  goBtn: {
    padding: "5px 12px",
    borderRadius: 5,
    border: "1px solid #374151",
    background: "#3b82f6",
    color: "white",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
  },
  quickRouteBtn: {
    padding: "3px 8px",
    borderRadius: 4,
    border: "1px solid #374151",
    background: "transparent",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: 11,
  },
  reloadBtn: {
    padding: "4px 8px",
    borderRadius: 4,
    border: "1px solid #374151",
    background: "transparent",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: 16,
    marginLeft: "auto",
  },

  // Review footer — collapsed (not started)
  reviewFooterCollapsed: {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 20px",
    borderTop: "2px solid #10b981",
    background: "#0f172a",
    color: "#f9fafb",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontSize: 13,
    zIndex: 99999,
    boxShadow: "0 -4px 16px rgba(0,0,0,0.6)",
  },
  // Review top bar — dominant bar when review active
  reviewTopBar: {
    display: "flex",
    flexDirection: "column" as const,
    borderBottom: "3px solid #10b981",
    background: "#0f172a",
    color: "#f9fafb",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  reviewTopRow1: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 16px",
  },
  reviewTopRow2: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 16px 10px",
  },
  reviewTopNavBtn: {
    padding: "8px 16px",
    borderRadius: 6,
    border: "1px solid #374151",
    background: "#1e293b",
    color: "#e2e8f0",
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 700,
    flexShrink: 0,
  },
  reviewTopCloseBtn: {
    background: "none",
    border: "1px solid #374151",
    borderRadius: 6,
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: 20,
    padding: "6px 12px",
    marginLeft: 8,
    flexShrink: 0,
  },
  reviewBadge: {
    background: "#10b981",
    color: "#022c22",
    padding: "3px 10px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    flexShrink: 0,
  },
  reviewStartBtn: {
    padding: "6px 16px",
    borderRadius: 5,
    border: "1px solid #10b981",
    background: "#10b981",
    color: "#022c22",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  reviewCompareBtn: {
    padding: "4px 10px",
    borderRadius: 4,
    border: "1px solid #374151",
    background: "transparent",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 400,
  },
  // kept for reference - now using reviewTopNavBtn
  reviewNavBtn_unused: {
  },

  // Matrix
  infoBanner: {
    padding: "8px 12px",
    background: "#1e3a5f",
    borderRadius: 6,
    fontSize: 12,
    color: "#93c5fd",
    marginBottom: 16,
  },
  matrixGroupLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#60a5fa",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: 6,
    marginTop: 4,
  },
  matrixRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: "8px 0",
    borderBottom: "1px solid #1f2937",
  },
  routeCode: {
    background: "#1f2937",
    padding: "2px 6px",
    borderRadius: 3,
    fontSize: 12,
    color: "#e2e8f0",
    fontFamily: "monospace",
  },
};
